/**
 * Users Service - Composition with createEntityService + hooks
 *
 * Uses ctx.createEntityService instead of inheritance to:
 * - Hash passwords in beforeCreate/beforeUpdate
 * - Validate email uniqueness
 * - Protect system roles
 * - Join user-role data in findAll/findById
 */

import type { ModuleContext, PaginatedResult, CollectionEntityService, EntityQuery } from '@gzl10/nexus-sdk'
import type {
  User,
  Role,
  UserWithoutPassword,
  UserWithRoles,
  RoleWithCounts
} from './users.types.js'
import { userEntity, roleEntity, userRoleEntity } from './users.entity.js'
// eslint-disable-next-line no-restricted-imports -- runtime/ needed until NEX-347 migrates entity-factory to http/
import { createEntityServiceAsync } from '../../runtime/entity-factory.js'

// ============================================================================
// HELPERS
// ============================================================================

/** User-role row from pivot table JOIN */
interface UserRoleRow {
  role_id: string
  role_name: string
  role_description: string | null
  role_created_at: Date
  role_updated_at: Date
}

// ============================================================================
// FACTORY FUNCTION
// ============================================================================

/**
 * Factory function to create the users service.
 * Uses composition with createEntityService + hooks.
 */
export async function createUsersService(ctx: ModuleContext) {
  const { errors, crypto: { hashPassword }, socket, generateId } = ctx.core
  const db = ctx.db.knex
  const { nowTimestamp } = ctx.db
  const { isUserConnected, getUserSocketCount } = socket
  const USERS_TABLE = userEntity.table
  const ROLES_TABLE = roleEntity.table
  const USER_ROLES_TABLE = userRoleEntity.table

  // ============================================================================
  // USERS SERVICE (composición)
  // ============================================================================

  const baseUsersService = await createEntityServiceAsync<User & Record<string, unknown>>(ctx, userEntity, {
    hooks: {
      beforeCreate: async (data) => {
        const processed = { ...data }
        const userType = (processed.type as string) ?? 'human'

        // Validaciones condicionales según tipo de usuario
        if (userType === 'human') {
          // Humans requieren email (password es opcional para OIDC users)
          if (!processed.email) {
            throw new errors.ValidationError('Email is required for human users')
          }
        }

        // 1. Hash password (solo si existe)
        if (processed.password) {
          processed.password = await hashPassword(processed.password)
        }

        // 2. Validar email único (solo si existe)
        if (processed.email) {
          const existing = await db(USERS_TABLE).where({ email: processed.email }).first()
          if (existing) {
            throw new errors.ConflictError('El email ya está en uso')
          }
        }

        return processed
      },

      beforeUpdate: async (id, data) => {
        const processed = { ...data }

        // 1. Hash password si cambia
        if (processed.password) {
          processed.password = await hashPassword(processed.password)
        }

        // 2. Validar email único (excluyendo actual)
        if (processed.email) {
          const existing = await db(USERS_TABLE)
            .where({ email: processed.email })
            .whereNot({ id })
            .first()
          if (existing) {
            throw new errors.ConflictError('El email ya está en uso')
          }
        }

        return processed
      }
    }
  }) as unknown as CollectionEntityService<User>

  // Helpers para mapear rows
  function excludePassword(user: User): UserWithoutPassword {
    const { password: _, ...rest } = user
    return rest
  }

  /** Map roles from pivot table rows to Role array */
  function mapRolesFromRows(rows: UserRoleRow[]): Role[] {
    return rows.map(row => ({
      id: row.role_id,
      name: row.role_name,
      description: row.role_description,
      created_at: row.role_created_at,
      updated_at: row.role_updated_at
    }))
  }

  /** Get roles for a user via pivot table */
  async function getRolesForUser(userId: string): Promise<Role[]> {
    const rows = await db(USER_ROLES_TABLE)
      .select(
        `${USER_ROLES_TABLE}.role_id`,
        `${ROLES_TABLE}.name as role_name`,
        `${ROLES_TABLE}.description as role_description`,
        `${ROLES_TABLE}.created_at as role_created_at`,
        `${ROLES_TABLE}.updated_at as role_updated_at`
      )
      .join(ROLES_TABLE, `${USER_ROLES_TABLE}.role_id`, `${ROLES_TABLE}.id`)
      .where(`${USER_ROLES_TABLE}.user_id`, userId) as UserRoleRow[]

    return mapRolesFromRows(rows)
  }

  /** Map user row + roles to UserWithRoles */
  function mapUserWithRoles(user: User, roles: Role[]): UserWithRoles {
    const { password, ...userWithoutPassword } = user
    const userId = user.id

    return {
      ...userWithoutPassword,
      has_password: password !== null && password !== undefined,
      roles,
      role_ids: roles.map(r => r.id), // Virtual field for multiselect input
      presence: {
        isOnline: isUserConnected(userId),
        socketCount: getUserSocketCount(userId)
      }
    }
  }

  // Métodos de usuarios con lógica personalizada
  const usersService = {
    /**
     * findAll with roles (multi-role)
     */
    async findAll(query?: EntityQuery): Promise<PaginatedResult<UserWithRoles>> {
      const pagination = ctx.db.getPagination(query)

      // Base query for users
      let qb = db(USERS_TABLE).select(`${USERS_TABLE}.*`)
      let countQb = db(USERS_TABLE).count('* as count')

      // Filter by role_id (users who have this role)
      const roleIdFilter = (query as Record<string, unknown>)?.['role_id'] ?? query?.filters?.['role_id']
      if (roleIdFilter) {
        const userIdsWithRole = db(USER_ROLES_TABLE)
          .select('user_id')
          .where('role_id', roleIdFilter)
        qb = qb.whereIn(`${USERS_TABLE}.id`, userIdsWithRole)
        countQb = countQb.whereIn('id', userIdsWithRole)
      }

      // Apply search filter (uses meta.searchable from entity definition)
      if (query?.search) {
        ctx.db.applySearchFilter(qb, userEntity, query.search)
        ctx.db.applySearchFilter(countQb, userEntity, query.search)
      }

      const countResult = await countQb.first<{ count: string | number }>()
      const total = Number(countResult?.count ?? 0)

      if (query?.sort) {
        qb = qb.orderBy(`${USERS_TABLE}.${query.sort}`, query.order ?? 'asc')
      } else {
        qb = qb.orderBy(`${USERS_TABLE}.created_at`, 'desc')
      }
      qb = qb.limit(pagination.limit).offset(pagination.offset)

      const users = await qb as User[]

      // Get all roles for these users in a single query
      const userIds = users.map(u => u.id)
      const rolesMap = new Map<string, Role[]>()

      if (userIds.length > 0) {
        const userRolesRows = await db(USER_ROLES_TABLE)
          .select(
            `${USER_ROLES_TABLE}.user_id`,
            `${USER_ROLES_TABLE}.role_id`,
            `${ROLES_TABLE}.name as role_name`,
            `${ROLES_TABLE}.description as role_description`,
            `${ROLES_TABLE}.created_at as role_created_at`,
            `${ROLES_TABLE}.updated_at as role_updated_at`
          )
          .join(ROLES_TABLE, `${USER_ROLES_TABLE}.role_id`, `${ROLES_TABLE}.id`)
          .whereIn(`${USER_ROLES_TABLE}.user_id`, userIds) as (UserRoleRow & { user_id: string })[]

        // Group roles by user_id
        for (const row of userRolesRows) {
          const userRoles = rolesMap.get(row.user_id) ?? []
          userRoles.push({
            id: row.role_id,
            name: row.role_name,
            description: row.role_description,
            created_at: row.role_created_at,
            updated_at: row.role_updated_at
          })
          rolesMap.set(row.user_id, userRoles)
        }
      }

      // Map users with their roles
      const items = users.map(user => mapUserWithRoles(user, rolesMap.get(user.id) ?? []))

      return ctx.db.buildPaginatedResult(items, total, pagination)
    },

    /**
     * findById with roles and presence (used by controller for GET /users/:id)
     */
    async findById(id: string): Promise<UserWithRoles> {
      const user = await baseUsersService.findById(id)
      if (!user) throw new errors.NotFoundError('Usuario')

      const roles = await getRolesForUser(id)
      return mapUserWithRoles(user, roles)
    },

    /**
     * @deprecated Use findById instead (now includes roles)
     */
    async findByIdWithRoles(id: string): Promise<UserWithRoles | null> {
      const user = await baseUsersService.findById(id)
      if (!user) return null

      const roles = await getRolesForUser(id)
      return mapUserWithRoles(user, roles)
    },

    /**
     * findByIdWithPassword - for authentication
     */
    async findByIdWithPassword(id: string): Promise<User | null> {
      return baseUsersService.findById(id)
    },

    /**
     * create with userId for audit (returns without password)
     * Supports role_ids virtual field for multi-role assignment
     */
    async create(data: Record<string, unknown>, userId?: string): Promise<UserWithoutPassword> {
      // Extract role_ids (virtual field, not in DB)
      const { role_ids, ...userData } = data
      const roleIds = Array.isArray(role_ids) ? role_ids as string[] : []

      const user = await baseUsersService.create({ ...userData, created_by: userId ?? null })

      // Assign roles if provided
      if (roleIds.length > 0) {
        await this.setRoles(user.id, roleIds)
      }

      return excludePassword(user)
    },

    /**
     * update with userId for audit (returns without password)
     * Supports role_ids virtual field for multi-role assignment
     */
    async update(id: string, data: Record<string, unknown>, userId?: string): Promise<UserWithoutPassword> {
      // Extract role_ids (virtual field, not in DB)
      const { role_ids, ...userData } = data

      const user = await baseUsersService.update(id, { ...userData, updated_by: userId ?? null })

      // Update roles if provided (undefined = no change, array = replace)
      if (role_ids !== undefined) {
        const roleIds = Array.isArray(role_ids) ? role_ids as string[] : []
        await this.setRoles(id, roleIds)
      }

      return excludePassword(user)
    },

    /**
     * delete
     */
    async delete(id: string) {
      return baseUsersService.delete(id)
    },

    // ========================================================================
    // Multi-Role Methods
    // ========================================================================

    /**
     * Get role IDs for a user
     */
    async getRoleIds(userId: string): Promise<string[]> {
      const rows = await db(USER_ROLES_TABLE)
        .select('role_id')
        .where({ user_id: userId }) as { role_id: string }[]
      return rows.map(r => r.role_id)
    },

    /**
     * Get role names for a user (useful for ability building)
     */
    async getRoleNames(userId: string): Promise<string[]> {
      const rows = await db(USER_ROLES_TABLE)
        .select(`${ROLES_TABLE}.name`)
        .join(ROLES_TABLE, `${USER_ROLES_TABLE}.role_id`, `${ROLES_TABLE}.id`)
        .where(`${USER_ROLES_TABLE}.user_id`, userId) as { name: string }[]
      return rows.map(r => r.name)
    },

    /**
     * Get full roles for a user
     */
    async getUserRoles(userId: string): Promise<Role[]> {
      return getRolesForUser(userId)
    },

    /**
     * Assign a role to a user
     */
    async assignRole(userId: string, roleId: string): Promise<void> {
      // Verify user exists
      const user = await db(USERS_TABLE).where({ id: userId }).first()
      if (!user) throw new errors.NotFoundError('Usuario')

      // Verify role exists
      const role = await db(ROLES_TABLE).where({ id: roleId }).first()
      if (!role) throw new errors.NotFoundError('Rol')

      // Check if already assigned
      const existing = await db(USER_ROLES_TABLE)
        .where({ user_id: userId, role_id: roleId })
        .first()
      if (existing) return // Already assigned, no-op

      await db(USER_ROLES_TABLE).insert({
        id: generateId(),
        user_id: userId,
        role_id: roleId
      })
    },

    /**
     * Remove a role from a user
     */
    async removeRole(userId: string, roleId: string): Promise<void> {
      const deleted = await db(USER_ROLES_TABLE)
        .where({ user_id: userId, role_id: roleId })
        .delete()

      if (deleted === 0) {
        throw new errors.NotFoundError('Asignación de rol')
      }
    },

    /**
     * Set user roles (replaces all existing roles)
     */
    async setRoles(userId: string, roleIds: string[]): Promise<void> {
      // Verify user exists
      const user = await db(USERS_TABLE).where({ id: userId }).first()
      if (!user) throw new errors.NotFoundError('Usuario')

      // Verify all roles exist
      if (roleIds.length > 0) {
        const existingRoles = await db(ROLES_TABLE)
          .whereIn('id', roleIds)
          .select('id') as { id: string }[]
        if (existingRoles.length !== roleIds.length) {
          throw new errors.NotFoundError('Uno o más roles no existen')
        }
      }

      await db.transaction(async (trx) => {
        // Remove all existing roles
        await trx(USER_ROLES_TABLE).where({ user_id: userId }).delete()

        // Insert new roles
        if (roleIds.length > 0) {
          const inserts = roleIds.map(roleId => ({
            id: generateId(),
            user_id: userId,
            role_id: roleId
          }))
          await trx(USER_ROLES_TABLE).insert(inserts)
        }
      })
    },

    // ========================================================================
    // GDPR / Privacy Methods
    // ========================================================================

    /**
     * Export all user data (GDPR Art. 15 - Right of Access)
     * Returns JSON with all personal data for the user
     */
    async exportUserData(userId: string): Promise<Record<string, unknown>> {
      // 1. User profile (without password)
      const user = await db(USERS_TABLE)
        .where({ id: userId })
        .first() as User | undefined

      if (!user) {
        throw new errors.NotFoundError('Usuario')
      }

      const { password: _, ...userProfile } = user

      // 2. Roles info (multi-role)
      const roles = await getRolesForUser(userId)

      // 3. Auth data (sessions + audit) via auth service (optional - avoids circular dep)
      type AuthServiceLike = { getUserAuthData: (userId: string) => Promise<{ sessions: unknown[]; auditLog: unknown[] }> }
      const authService = ctx.services.getOptional<AuthServiceLike>('auth')
      const { sessions, auditLog } = authService
        ? await authService.getUserAuthData(userId)
        : { sessions: [], auditLog: [] }

      // 4. Uploaded files via storage service (optional - may not be loaded)
      type StorageServiceLike = { getUserFiles: (userId: string) => Promise<unknown[]> }
      const storageService = ctx.services.getOptional<StorageServiceLike>('storage')
      const files = storageService ? await storageService.getUserFiles(userId) : []

      return {
        exportDate: new Date().toISOString(),
        profile: userProfile,
        roles: roles.map(r => ({ id: r.id, name: r.name, description: r.description })),
        sessions,
        auditLog,
        files,
        _meta: {
          format: 'GDPR Art. 15 Data Export',
          retentionPolicy: {
            auditLog: '90 days',
            sessions: '7 days (auto-expire)'
          }
        }
      }
    },

    /**
     * Delete user account and all associated data (GDPR Art. 17 - Right to Erasure)
     * Performs cascade deletion with audit trail anonymization
     */
    async deleteAccount(userId: string): Promise<{ deleted: true; summary: Record<string, number> }> {
      const user = await db(USERS_TABLE).where({ id: userId }).first() as User | undefined
      if (!user) {
        throw new errors.NotFoundError('Usuario')
      }

      const summary: Record<string, number> = {}

      // 1. Delete user roles (own table - users module owns this)
      const rolesDeleted = await db(USER_ROLES_TABLE)
        .where({ user_id: userId })
        .delete()
      summary['userRoles'] = rolesDeleted

      // 2. Revoke all sessions via auth service (optional - avoids circular dep)
      type AuthServiceLike = { revokeAllUserSessions: (userId: string) => Promise<number>; anonymizeUserAudit: (userId: string) => Promise<number> }
      const authService = ctx.services.getOptional<AuthServiceLike>('auth')
      summary['refreshTokens'] = authService ? await authService.revokeAllUserSessions(userId) : 0

      // 3. Anonymize audit log via auth service
      summary['auditRecordsAnonymized'] = authService ? await authService.anonymizeUserAudit(userId) : 0

      // 4. Delete uploaded files via storage service (optional)
      type StorageServiceLike = { deleteUserFiles: (userId: string) => Promise<number> }
      const storageService = ctx.services.getOptional<StorageServiceLike>('storage')
      summary['filesDeleted'] = storageService ? await storageService.deleteUserFiles(userId) : 0

      // 5. Delete user record
      await db(USERS_TABLE).where({ id: userId }).delete()
      summary['userDeleted'] = 1

      // 6. Emit audit event for account deletion
      ctx.events.notify('audit.log', {
        source: 'core:users',
        action: 'account_deleted',
        actorId: userId,
        resourceType: 'user',
        resourceId: userId
      })

      // 7. Emit event for custom hooks (e.g., external systems cleanup)
      ctx.events.notify('user:deleted', { userId, email: user.email, deletedAt: new Date().toISOString() })

      return { deleted: true as const, summary }
    },

    /**
     * Record user consent (GDPR compliance)
     * Updates consent_date, consent_version, and marketing_opt_in
     * Emits 'user:consent' event for audit logging
     */
    async recordConsent(
      userId: string,
      version: string,
      marketingOptIn: boolean,
      meta?: { ipAddress?: string; userAgent?: string }
    ): Promise<{ recorded: true; consentDate: string }> {
      const user = await db(USERS_TABLE).where({ id: userId }).first() as User | undefined
      if (!user) {
        throw new errors.NotFoundError('Usuario')
      }

      const consentDate = new Date().toISOString()

      await db(USERS_TABLE).where({ id: userId }).update({
        consent_date: consentDate,
        consent_version: version,
        marketing_opt_in: marketingOptIn,
        updated_at: nowTimestamp(db)
      })

      // Emit event with all data for compliance audit log
      ctx.events.notify('user:consent', {
        userId,
        email: user.email,
        version,
        marketingOptIn,
        consentDate,
        ipAddress: meta?.ipAddress,
        userAgent: meta?.userAgent
      })

      return { recorded: true, consentDate }
    },

    // ========================================================================
    // Bot/Service Account Helpers
    // ========================================================================

    /**
     * Create a service account (no email/password, no default role)
     */
    async createServiceAccount(
      name: string,
      metadata?: Record<string, unknown>
    ): Promise<UserWithoutPassword> {
      const userId = generateId()
      await db(USERS_TABLE).insert({
        id: userId,
        type: 'service',
        email: null,
        password: null,
        name,
        metadata: metadata ? JSON.stringify(metadata) : null,
        marketing_opt_in: false,
        created_at: nowTimestamp(db),
        updated_at: nowTimestamp(db)
      })

      const user = await db(USERS_TABLE).where({ id: userId }).first() as User
      return excludePassword(user)
    },

    /**
     * Create a bot user (no email/password, linked to owner, no default role)
     */
    async createBotUser(
      name: string,
      ownerId: string,
      metadata?: Record<string, unknown>
    ): Promise<UserWithoutPassword> {
      // Verify owner exists and is human
      const owner = await db(USERS_TABLE).where({ id: ownerId }).first() as User | undefined
      if (!owner) {
        throw new errors.NotFoundError('Owner user')
      }
      if (owner.type !== 'human') {
        throw new errors.ValidationError('Bot owner must be a human user')
      }

      const userId = generateId()
      const botMetadata = {
        ...metadata,
        owner_id: ownerId
      }

      await db(USERS_TABLE).insert({
        id: userId,
        type: 'bot',
        email: null,
        password: null,
        name,
        metadata: JSON.stringify(botMetadata),
        marketing_opt_in: false,
        created_by: ownerId,
        created_at: nowTimestamp(db),
        updated_at: nowTimestamp(db)
      })

      const user = await db(USERS_TABLE).where({ id: userId }).first() as User
      return excludePassword(user)
    }
  }

  // ============================================================================
  // ROLES SERVICE (composición)
  // ============================================================================

  const baseRolesService = await createEntityServiceAsync<Role & Record<string, unknown>>(ctx, roleEntity, {
    hooks: {
      beforeCreate: async (data) => {
        if (data.name) {
          const existing = await db(ROLES_TABLE).where({ name: data.name }).first()
          if (existing) {
            throw new errors.ConflictError('Ya existe un rol con ese nombre')
          }
        }
        return data
      },

      beforeUpdate: async (id, data) => {
        const role = await db(ROLES_TABLE).where({ id }).first() as Role | undefined

        if (data.name && data.name !== role?.name) {
          const existing = await db(ROLES_TABLE).where({ name: data.name }).first()
          if (existing) {
            throw new errors.ConflictError('Ya existe un rol con ese nombre')
          }
        }

        return data
      },

      beforeDelete: async (id) => {
        const role = await db(ROLES_TABLE).where({ id }).first() as Role | undefined
        if (!role) return

        // Check if any users have this role assigned
        const usersCount = await db(USER_ROLES_TABLE)
          .where({ role_id: id })
          .count('* as count')
          .first<{ count: string | number }>()

        if (Number(usersCount?.count ?? 0) > 0) {
          throw new errors.ConflictError('No se puede eliminar un rol con usuarios asignados')
        }
      }
    }
  }) as unknown as CollectionEntityService<Role>

  const rolesService = {
    /**
     * findAll with user counts
     */
    async findAll(query?: EntityQuery): Promise<PaginatedResult<RoleWithCounts>> {
      const pagination = ctx.db.getPagination(query)

      // Count users per role via pivot table
      const usersCountSubquery = db(USER_ROLES_TABLE)
        .count('*')
        .whereRaw(`${USER_ROLES_TABLE}.role_id = ${ROLES_TABLE}.id`)
        .as('users_count')

      const baseQuery = db(ROLES_TABLE)
        .select(`${ROLES_TABLE}.*`, usersCountSubquery)

      const countQuery = db(ROLES_TABLE).count('* as count')

      // Apply search filter (uses meta.searchable from entity definition)
      if (query?.search) {
        ctx.db.applySearchFilter(baseQuery, roleEntity, query.search)
        ctx.db.applySearchFilter(countQuery, roleEntity, query.search)
      }

      const [roles, countResult] = await Promise.all([
        baseQuery.clone().orderBy(query?.sort ?? 'name', query?.sort ? (query.order ?? 'asc') : 'asc').limit(pagination.limit).offset(pagination.offset),
        countQuery.first<{ count: string | number }>()
      ])

      const total = Number(countResult?.count ?? 0)
      const items = (roles as Record<string, unknown>[]).map((role) => ({
        ...role,
        users_count: Number(role['users_count'] ?? 0)
      })) as RoleWithCounts[]

      return ctx.db.buildPaginatedResult(items, total, pagination)
    },

    /**
     * findById
     */
    async findById(id: string): Promise<Role> {
      const role = await baseRolesService.findById(id)
      if (!role) throw new errors.NotFoundError('Rol')
      return role
    },

    /**
     * findByName
     */
    async findByName(name: string): Promise<Role | undefined> {
      return db(ROLES_TABLE).where({ name }).first() as Promise<Role | undefined>
    },

    /**
     * create role
     * Note: roles table has no audit fields (created_by/updated_by)
     */
    async create(data: Record<string, unknown>) {
      return baseRolesService.create(data)
    },

    /**
     * update role
     * Note: roles table has no audit fields (created_by/updated_by)
     */
    async update(id: string, data: Record<string, unknown>) {
      return baseRolesService.update(id, data)
    },

    /**
     * delete
     */
    async delete(id: string) {
      return baseRolesService.delete(id)
    }
  }

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // Definition para compatibilidad con BaseEntityService (requerido por createEntityController)
    definition: userEntity,

    // Users methods
    findAll: usersService.findAll,
    findById: usersService.findById,
    findByIdWithRoles: usersService.findByIdWithRoles,
    findByIdWithPassword: usersService.findByIdWithPassword,
    create: usersService.create,
    update: usersService.update,
    delete: usersService.delete,

    // Multi-role methods
    getRoleIds: usersService.getRoleIds,
    getRoleNames: usersService.getRoleNames,
    getUserRoles: usersService.getUserRoles,
    assignRole: usersService.assignRole,
    removeRole: usersService.removeRole,
    setRoles: usersService.setRoles,

    // GDPR / Privacy methods
    exportUserData: usersService.exportUserData,
    deleteAccount: usersService.deleteAccount,
    recordConsent: usersService.recordConsent,

    // Bot/Service account helpers
    createServiceAccount: usersService.createServiceAccount,
    createBotUser: usersService.createBotUser,

    // Roles nested service (con definition para createEntityController)
    roles: {
      definition: roleEntity,
      ...rolesService
    }
  }
}
